import { StrictMode, Component, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

import { App } from "@/App";
import { setTokenGetter } from "@/lib/api";
import "@/index.css";

function TokenBridge() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  useEffect(() => {
    if (isAuthenticated) setTokenGetter(getAccessTokenSilently);
  }, [isAuthenticated, getAccessTokenSilently]);
  return null;
}

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";

class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      const e = this.state.error as Error;
      return (
        <div style={{ padding: 32, fontFamily: "monospace", color: "#f87171", background: "#0a0a0a", minHeight: "100vh" }}>
          <h2 style={{ marginBottom: 16 }}>Runtime crash — {e.message}</h2>
          <pre style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{e.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/login`,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? import.meta.env.VITE_API_URL,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <TokenBridge />
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Auth0Provider>
    </ErrorBoundary>
  </StrictMode>,
);
